import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

import { BookingWidget } from "@/modules/coaching/booking/components/BookingWidget";
import { BookingContextProvider } from "@/modules/coaching/booking/context/BookingContext";
import { clearStaleEmbedWizardSessionKeys } from "@/modules/coaching/booking/hooks/useBookingSessionStorage";
import { ENTRY_POINTS } from "@/modules/tracking/consts/EntryPoints";
import { registerBookingSuperProperties } from "@/modules/tracking/utils/registerBookingSuperProperties";

// Member PII (name, email, phone) is intentionally NOT accepted via URL search
// params to avoid leaking it through Referer headers, analytics, or error
// tracking. It is delivered by the host via window.__BOOKING_IFRAME_VARS__
// (see useBookingIframeVars) and seeded into BookingContext from there.
const bookingWidgetParams = z.object({
  coachId: z.number().optional(),
  coachName: z.string().optional(),
  customerId: z.number().optional(),
  sessionId: z.coerce.number().int().positive().optional(),
  // ISO timestamp of the original session start, used by reschedule analytics.
  // Hosts that own the rescheduled session (Caladan, Glepnir) pass it alongside
  // sessionId so original_session_start_time is populated on reschedule events.
  originalSessionStart: z.string().datetime({ offset: true }).optional(),
  client: z.enum(ENTRY_POINTS).optional(),
  // Glepnir passes clearOnLoad=true to guarantee a fresh wizard state on each
  // embed load, clearing any stale date/time from a previous session before
  // useBookingSessionStorage rehydrates. Caladan and direct browser access omit
  // this param to preserve mid-flow state across refreshes.
  clearOnLoad: z.boolean().optional(),
});

function BookingRoute() {
  const {
    coachId,
    coachName,
    customerId,
    sessionId,
    originalSessionStart,
    client,
  } = Route.useSearch();
  return (
    <BookingContextProvider
      initialData={{
        coachId,
        coachName,
        customerId,
        rescheduleSessionId: sessionId,
        originalSessionStart,
        entryPoint: client,
      }}
    >
      <BookingWidget enableIframeVars />
    </BookingContextProvider>
  );
}

export const Route = createFileRoute("/embed/book/")({
  validateSearch: bookingWidgetParams,
  // When clearOnLoad (e.g. Glepnir iframe) runs before the route component so
  // session wizard keys are not rehydrated stale; CLEAR postMessage can arrive too late.
  beforeLoad: ({ search }) => {
    if (typeof window !== "undefined" && search.clearOnLoad) {
      clearStaleEmbedWizardSessionKeys();
    }
    registerBookingSuperProperties({
      customerId: search.customerId,
      entryPoint: search.client,
    });
  },
  component: BookingRoute,
});
